import type { HelpCenterArticle } from "@productlane/zero"

type RedirectSource = Pick<
	HelpCenterArticle,
	| "id"
	| "title"
	| "urlName"
	| "previousUrlNames"
	| "published"
	| "visibility"
	| "kind"
	| "isDeleted"
> & { subpages?: readonly RedirectSource[] }

export interface FolderRedirect {
	articleId: string
	from: string
	to: string
	title: string
}

// The portal answers an old path only for a published public doc, so any other
// article's history redirects nowhere and is not listed.
export function liveOldUrlNames(article: {
	published: boolean
	visibility: RedirectSource["visibility"]
	previousUrlNames?: readonly string[] | null
}): readonly string[] {
	if (!article.published || article.visibility !== "PUBLIC") return []
	return article.previousUrlNames ?? []
}

export function folderRedirects(
	articles: readonly RedirectSource[],
): FolderRedirect[] {
	const rows: FolderRedirect[] = []
	const visit = (list: readonly RedirectSource[]) => {
		for (const article of list) {
			if (!article.isDeleted && article.kind === "DOC") {
				for (const from of liveOldUrlNames(article)) {
					rows.push({
						articleId: article.id,
						from,
						to: article.urlName,
						title: article.title,
					})
				}
			}
			if (article.subpages) visit(article.subpages)
		}
	}
	visit(articles)
	return rows
}

export type FolderRedirectItem =
	| { kind: "article"; key: string; title: string; to: string }
	| { kind: "redirect"; key: string; row: FolderRedirect }

// One heading per article, its old paths under it, in first-seen order.
export function groupRedirects(
	rows: readonly FolderRedirect[],
): FolderRedirectItem[] {
	const byArticle = new Map<string, FolderRedirect[]>()
	for (const row of rows) {
		const list = byArticle.get(row.articleId)
		if (list) list.push(row)
		else byArticle.set(row.articleId, [row])
	}
	const items: FolderRedirectItem[] = []
	for (const [articleId, list] of byArticle) {
		const first = list[0]
		if (!first) continue
		items.push({
			kind: "article",
			key: `article:${articleId}`,
			title: first.title,
			to: first.to,
		})
		for (const row of list) {
			items.push({
				kind: "redirect",
				key: `redirect:${articleId}:${row.from}`,
				row,
			})
		}
	}
	return items
}

export function matchesRedirect(row: FolderRedirect, query: string) {
	const needle = query.trim().toLowerCase()
	if (!needle) return true
	return (
		row.from.toLowerCase().includes(needle) ||
		row.to.toLowerCase().includes(needle) ||
		row.title.toLowerCase().includes(needle)
	)
}
